import { v4 as uuidv4 } from "uuid";
import { getLocale } from "@app/paraglide/runtime.js";
import tcLib from "@cclib/lib/timecode.js";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
import { projectState } from "./projectStore.svelte.js";
import { eventGroupState, isEventGroupLocked } from "./eventGroupStore.svelte.js";
import { playerState } from "./playerStore.svelte.js";
import { authState } from "./authStore.svelte.js";
import { listChats, saveChat, deleteChat } from "@app/lib/assistant/chatDb.js";
import { buildProjectContext } from "@app/lib/assistant/projectContext.js";
import { titleFromPrompt } from "@app/lib/assistant/historyGroups.js";
import {
    addAgentPlugin,
    getAssistantStatus,
    setAgentOverageCap,
    streamAssistantAnswer,
    streamAgentStep,
} from "@app/lib/assistant/assistantClient.js";
import { runAgentTurn } from "@app/lib/assistant/agent/agentLoop.js";
import { executeToolUse } from "@app/lib/assistant/agent/registry.js";
import { createStoreContext } from "@app/lib/assistant/agent/storeContext.js";
import { clearAgentLocks } from "@app/lib/assistant/agent/agentEditLock.svelte.js";
import { agentConversationMark, rewindAgentConversation } from "@app/lib/assistant/checkpoints/conversation.js";
import {
    applyRestore,
    createCheckpoint,
    downloadCheckpointCopy,
    prepareRestore,
} from "@app/lib/assistant/checkpoints/checkpointService.js";
import {
    beginHistoryTurn,
    endHistoryTurn,
    isTurnAtUndoTip,
    isUndoBlockedByLock,
    undoHistory,
} from "./historyStore.svelte.js";

const AGENT_MODEL_KEY = "cc-assistant-agent-model";

function loadAgentModel() {
    try {
        return localStorage.getItem(AGENT_MODEL_KEY) === "deep" ? "deep" : "standard";
    } catch {
        return "standard";
    }
}

export const assistantState = $state({
    projectId: null,
    chats: [],
    activeChatId: null,
    streamingChatId: null,
    mode: "ask", // "ask" | "agent"
    agentModel: loadAgentModel(), // "standard" (Sonnet) | "deep" (Opus, uses credits about 2× faster)
    status: null, // { plan, limit, used, remaining, agent: { allowed, creditsUsed, creditsLimit, creditsRemaining } }
    statusLoading: false,
    statusError: null,
    /**
     * Text handed to the composer, which takes it and clears it: a string from
     * "Edit & restore" (Agent mode), or { text, mode } from an example prompt.
     */
    composerDraft: null,
    /** Checkpoint restore in progress (blocks sending and other restores). */
    restoring: false,
});

let abortController = null;
let loadToken = 0;
/** @type {Map<string, (approved: boolean) => void>} tool_use id -> pending confirmation */
const pendingConfirmations = new Map();

export function getActiveChat() {
    return assistantState.chats.find((chat) => chat.id === assistantState.activeChatId) ?? null;
}

export async function loadAssistantChats(projectId) {
    const token = ++loadToken;
    assistantState.projectId = projectId ?? null;
    assistantState.activeChatId = null;
    assistantState.chats = [];
    if (!projectId) return;
    try {
        const chats = await listChats(projectId);
        if (token !== loadToken) return;
        assistantState.chats = chats;
        const latest = [...chats].sort((a, b) => b.updatedAt - a.updatedAt)[0];
        assistantState.activeChatId = latest?.id ?? null;
    } catch (error) {
        console.error("Failed to load assistant chats", error);
    }
}

export function startNewChat() {
    assistantState.activeChatId = null;
}

export function selectAssistantChat(id) {
    assistantState.activeChatId = id;
}

export async function renameAssistantChat(id, title) {
    const chat = assistantState.chats.find((item) => item.id === id);
    const trimmed = String(title ?? "").trim();
    if (!chat || !trimmed) return;
    chat.title = trimmed;
    chat.updatedAt = Date.now();
    await saveChat(chat);
}

export async function deleteAssistantChat(id) {
    if (assistantState.streamingChatId === id) {
        stopAssistantMessage();
    }
    await deleteChat(id);
    assistantState.chats = assistantState.chats.filter((chat) => chat.id !== id);
    if (assistantState.activeChatId === id) {
        assistantState.activeChatId = null;
    }
}

export async function refreshAssistantStatus() {
    assistantState.statusLoading = true;
    assistantState.statusError = null;
    try {
        assistantState.status = await getAssistantStatus();
    } catch (error) {
        assistantState.statusError = error?.code ?? "unknown";
    } finally {
        assistantState.statusLoading = false;
    }
}

function formatTc(seconds) {
    return tcLib.secToTc(
        seconds + (projectState.incode || 0),
        projectState.frameRate,
        projectState.dropFrame,
    );
}

export function getProjectContextSummary() {
    return buildProjectContext({
        project: projectState,
        groups: eventGroupState,
        playerTime: playerState.time,
        auth: authState,
        isLocked: (index) => isEventGroupLocked(index),
        formatTc,
        toPlainText: (html) => convertToPlainText(html),
        uiLanguage: getLocale(),
    });
}

function toHistory(messages) {
    return messages
        .filter((message) => !message.errorCode && message.content)
        .map((message) => ({ role: message.role, content: message.content }));
}

function errorCodeFor(error) {
    const code = String(error?.code ?? "").replace(/^functions\//, "");
    if (error?.name === "AbortError" || code === "cancelled") return "cancelled";
    return code || "unknown";
}

function ensureActiveChat(text) {
    let chat = getActiveChat();
    if (!chat) {
        const now = Date.now();
        assistantState.chats.push({
            id: uuidv4(),
            projectId: projectState.id,
            title: titleFromPrompt(text),
            messages: [],
            createdAt: now,
            updatedAt: now,
        });
        chat = assistantState.chats[assistantState.chats.length - 1];
        assistantState.activeChatId = chat.id;
    }
    return chat;
}

export function isAgentAvailable() {
    return Boolean(assistantState.status?.agent?.allowed);
}

/** Add the AI Agent plugin (billing owner), then show the new status. */
export async function addAgentPluginAndRefresh() {
    const result = await addAgentPlugin();
    if (assistantState.status && result?.agent) {
        assistantState.status.agent = result.agent;
    } else {
        await refreshAssistantStatus();
    }
    return result;
}

/** Change the monthly overage limit (billing owner). */
export async function updateAgentOverageCap(capUsd) {
    const result = await setAgentOverageCap(capUsd);
    if (assistantState.status && result?.agent) {
        assistantState.status.agent = result.agent;
    }
    return result;
}

export function setAssistantMode(mode) {
    if (assistantState.streamingChatId) return;
    assistantState.mode = mode === "agent" && isAgentAvailable() ? "agent" : "ask";
}

export function setAgentModel(model) {
    if (assistantState.streamingChatId) return;
    assistantState.agentModel = model === "deep" ? "deep" : "standard";
    try {
        localStorage.setItem(AGENT_MODEL_KEY, assistantState.agentModel);
    } catch {
        // Storage unavailable (private window): the choice lasts for this session only.
    }
}

export async function sendAssistantMessage(question) {
    const text = String(question ?? "").trim();
    if (!text || assistantState.streamingChatId || assistantState.restoring) return;
    if (assistantState.mode === "agent" && isAgentAvailable()) {
        return sendAgentMessage(text);
    }

    const projectId = projectState.id;
    const chat = ensureActiveChat(text);

    const history = toHistory(chat.messages);
    chat.messages.push({ id: uuidv4(), role: "user", content: text, ts: Date.now() });
    chat.messages.push({
        id: uuidv4(),
        role: "assistant",
        content: "",
        citations: [],
        ts: Date.now(),
        streaming: true,
    });
    const reply = chat.messages[chat.messages.length - 1];
    chat.updatedAt = Date.now();
    saveChat(chat).catch((error) => console.error("Failed to save assistant chat", error));

    abortController = new AbortController();
    assistantState.streamingChatId = chat.id;

    const addCitation = (citation) => {
        if (!citation?.url || reply.citations.some((item) => item.url === citation.url)) return;
        reply.citations.push({ url: citation.url, title: citation.title });
    };

    try {
        const result = await streamAssistantAnswer(
            {
                mode: "ask",
                question: text,
                history,
                projectId,
                projectContext: getProjectContextSummary(),
            },
            {
                signal: abortController.signal,
                onText: (delta) => {
                    reply.content += delta;
                },
                onCitation: addCitation,
            },
        );
        if (result?.text && result.text.length > reply.content.length) {
            reply.content = result.text;
        }
        (result?.citations ?? []).forEach(addCitation);
        if (assistantState.status && Number.isFinite(result?.remaining)) {
            assistantState.status.remaining = result.remaining;
            assistantState.status.used = result.limit - result.remaining;
        }
    } catch (error) {
        const code = errorCodeFor(error);
        if (code === "cancelled") {
            reply.stopped = true;
        } else {
            console.error("Assistant request failed", error);
            reply.errorCode = code;
            if (code === "resource-exhausted" && assistantState.status) {
                assistantState.status.remaining = 0;
            }
        }
    } finally {
        reply.streaming = false;
        chat.updatedAt = Date.now();
        assistantState.streamingChatId = null;
        abortController = null;
        await saveChat(chat).catch((error) => console.error("Failed to save assistant chat", error));
    }
}

export function stopAssistantMessage() {
    abortController?.abort();
    resolveAllConfirmations(false);
}

/* Agent mode */

/** Server error details carry a reason (credits, step_limit, conversation_too_long, …). */
function agentErrorCode(error) {
    const reason = error?.details?.reason;
    return typeof reason === "string" ? reason : errorCodeFor(error);
}

function resolveAllConfirmations(approved) {
    for (const resolve of pendingConfirmations.values()) {
        resolve(approved);
    }
    pendingConfirmations.clear();
}

/** Approve or cancel the inline confirmation card of a pending tool call. */
export function resolveAgentConfirmation(toolUseId, approved) {
    const resolve = pendingConfirmations.get(toolUseId);
    if (!resolve) return;
    pendingConfirmations.delete(toolUseId);
    resolve(Boolean(approved));
}

function updateActivity(reply, toolUseId, patch) {
    const index = reply.activities.findIndex((activity) => activity.toolUseId === toolUseId);
    if (index === -1) {
        reply.activities.push({ toolUseId, ...patch });
    } else {
        reply.activities[index] = { ...reply.activities[index], ...patch };
    }
}

const CREDIT_FIELDS = [
    "creditsUsed",
    "creditsLimit",
    "creditsRemaining",
    "overageUsed",
    "overageCostUsd",
    "overageEnabled",
    "capUsd",
    "periodEnd",
    "trial",
];

function applyCredits(credits) {
    const agent = assistantState.status?.agent;
    if (!agent || !credits) return;
    for (const field of CREDIT_FIELDS) {
        if (credits[field] !== undefined) agent[field] = credits[field];
    }
}

const TURN_NOTICES = new Set(["max_tokens", "step_limit", "refused"]);

/**
 * Run one Agent turn. All writes of the turn become a single undo step named
 * "AI: <prompt>", even when the turn is stopped or fails.
 */
export async function sendAgentMessage(prompt) {
    const text = String(prompt ?? "").trim();
    if (!text || assistantState.streamingChatId || assistantState.restoring || !isAgentAvailable()) return;

    const projectId = projectState.id;
    const chat = ensureActiveChat(text);
    const turnId = uuidv4();
    const historyName = `AI: ${titleFromPrompt(text, 40)}`;
    // Fixed for the whole turn, so every step runs (and caches) on one model.
    const agentModel = assistantState.agentModel;

    const userMessage = {
        id: uuidv4(),
        role: "user",
        content: text,
        ts: Date.now(),
        agentBefore: agentConversationMark(chat.agentMessages),
    };
    // Captured before any write of this turn (notes §3.5); saved in the background.
    const checkpoint = createCheckpoint({ projectId, chatId: chat.id, messageId: userMessage.id });
    userMessage.checkpointId = checkpoint.id;
    chat.messages.push(userMessage);
    const savedUserMessage = chat.messages[chat.messages.length - 1];
    checkpoint.ready.catch((error) => {
        console.error("Failed to save checkpoint", error);
        savedUserMessage.checkpointExpired = true;
    });

    // After a restore the model's earlier tool results may be stale: tell it once.
    const modelPrompt = chat.pendingRestoreNote ? `${chat.pendingRestoreNote}\n\n${text}` : text;
    delete chat.pendingRestoreNote;

    chat.messages.push({
        id: uuidv4(),
        role: "assistant",
        mode: "agent",
        agentModel,
        turnId,
        content: "",
        activities: [],
        ts: Date.now(),
        streaming: true,
    });
    const reply = chat.messages[chat.messages.length - 1];
    chat.updatedAt = Date.now();
    saveChat(chat).catch((error) => console.error("Failed to save assistant chat", error));

    abortController = new AbortController();
    const { signal } = abortController;
    assistantState.streamingChatId = chat.id;

    const working = $state.snapshot(chat.agentMessages ?? []);
    const ctx = createStoreContext({ turnId, historyName });
    beginHistoryTurn();

    const confirm = (toolUseId, activity) => {
        updateActivity(reply, toolUseId, { ...activity, status: "awaiting_confirmation" });
        if (signal.aborted) return Promise.resolve(false);
        return new Promise((resolve) => pendingConfirmations.set(toolUseId, resolve));
    };

    try {
        const outcome = await runAgentTurn({
            messages: working,
            prompt: modelPrompt,
            signal,
            callStep: async (messages, { step }) => {
                let needsBreak = Boolean(reply.content);
                const result = await streamAgentStep(
                    {
                        mode: "agent",
                        agentModel,
                        messages: $state.snapshot(messages),
                        projectContext: step === 1 ? getProjectContextSummary() : undefined,
                        chatId: chat.id,
                        turnId,
                        projectId,
                    },
                    {
                        signal,
                        onText: (delta) => {
                            if (needsBreak) {
                                reply.content += "\n\n";
                                needsBreak = false;
                            }
                            reply.content += delta;
                        },
                        onToolUse: ({ id, name }) => updateActivity(reply, id, { name, status: "pending" }),
                    },
                );
                applyCredits(result?.credits);
                return result;
            },
            runTool: async (block) => {
                if (projectState.id !== projectId) {
                    // The user opened another project: never write into it.
                    abortController?.abort();
                    return { type: "tool_result", tool_use_id: block.id, content: "Not run: the project was closed.", is_error: true };
                }
                updateActivity(reply, block.id, { name: block.name, status: "running" });
                const { result, activity } = await executeToolUse(block, ctx, {
                    confirm: (request) => confirm(block.id, request.activity),
                });
                pendingConfirmations.delete(block.id);
                updateActivity(reply, block.id, { ...activity, turnId });
                return result;
            },
        });

        if (outcome.status === "stopped") {
            reply.stopped = true;
        } else if (outcome.status === "error") {
            console.error("Agent request failed", outcome.error);
            reply.errorCode = agentErrorCode(outcome.error);
            if (reply.errorCode === "credits" || reply.errorCode === "overage_cap") applyCredits(outcome.error?.details);
        } else if (TURN_NOTICES.has(outcome.status)) {
            reply.notice = outcome.status;
        }
    } finally {
        reply.undoSteps = endHistoryTurn(turnId, historyName);
        clearAgentLocks();
        resolveAllConfirmations(false);
        reply.activities.forEach((activity, index) => {
            if (["pending", "running", "awaiting_confirmation"].includes(activity.status)) {
                reply.activities[index] = { ...activity, status: "cancelled" };
            }
        });
        reply.streaming = false;
        chat.agentMessages = working;
        chat.updatedAt = Date.now();
        assistantState.streamingChatId = null;
        abortController = null;
        await saveChat(chat).catch((error) => console.error("Failed to save assistant chat", error));
    }
}

/** Whether the activity card's Undo would revert exactly this AI turn. */
export function canUndoAgentTurn(turnId) {
    return !assistantState.streamingChatId && isTurnAtUndoTip(turnId) && !isUndoBlockedByLock();
}

export function undoAgentTurn(turnId) {
    if (canUndoAgentTurn(turnId)) {
        undoHistory();
    }
}

/** Show and select the events an activity changed (those that still exist). */
export function reviewAgentActivity(activity) {
    const groupIndex = eventGroupState.findIndex((group) => group.id === activity.groupId);
    if (groupIndex === -1) return;
    projectState.selected = groupIndex;
    const ids = new Set((activity.changes ?? []).filter((change) => change.type !== "deleted").map((change) => change.id));
    const indexes = [];
    eventGroupState[groupIndex].events.forEach((event, index) => {
        if (ids.has(event.id)) indexes.push(index);
    });
    eventGroupState[groupIndex].selected = indexes;
}

/* Checkpoints and restore (notes/ai-assistant-phase1.md §3.5) */

const RESTORE_NOTE =
    "[Note for the assistant: since your last turn the user restored the project to an earlier checkpoint. " +
    "Results of earlier tool calls may be out of date; read the project again before editing.]";

function findActiveChatMessage(messageId) {
    const chat = getActiveChat();
    const index = chat ? chat.messages.findIndex((message) => message.id === messageId) : -1;
    return index === -1 ? null : { chat, index, message: chat.messages[index] };
}

function saveChatQuietly(chat) {
    return saveChat(chat).catch((error) => console.error("Failed to save assistant chat", error));
}

export function canRestoreCheckpoint() {
    return !assistantState.streamingChatId && !assistantState.restoring;
}

async function prepareFromCheckpoint(found, checkpointId) {
    if (!found || !checkpointId || !canRestoreCheckpoint()) return null;
    const prepared = await prepareRestore(checkpointId);
    return prepared ?? { expired: true };
}

/**
 * What restoring to the checkpoint taken before this prompt would change.
 * @returns {Promise<null | { expired: true } | object>} null when restoring is not possible now
 */
export async function prepareMessageRestore(messageId) {
    const found = findActiveChatMessage(messageId);
    const prepared = await prepareFromCheckpoint(found, found?.message.checkpointId);
    if (prepared?.expired) {
        found.message.checkpointExpired = true;
        saveChatQuietly(found.chat);
    }
    return prepared;
}

/** What "Redo restore" on a restore marker would change. */
export async function prepareRedoRestore(eventId) {
    const found = findActiveChatMessage(eventId);
    const prepared = await prepareFromCheckpoint(found, found?.message.undoCheckpointId);
    if (prepared?.expired) {
        found.message.undoCheckpointId = null;
        saveChatQuietly(found.chat);
    }
    return prepared;
}

async function runRestore(chat, prepared, name) {
    assistantState.restoring = true;
    try {
        return await applyRestore(prepared, { chatId: chat.id, name });
    } finally {
        assistantState.restoring = false;
    }
}

/**
 * Restore the project to the checkpoint before a prompt (one undo step).
 * With `edit`, also rewind the conversation to before that prompt, keep the
 * later messages as a collapsed "previous attempt" and put the prompt back in
 * the composer.
 * @param {string} messageId
 * @param {object} prepared  from prepareMessageRestore
 * @param {{ edit?: boolean }} [options]
 * @returns {Promise<boolean>}
 */
export async function completeMessageRestore(messageId, prepared, { edit = false } = {}) {
    const found = findActiveChatMessage(messageId);
    if (!found || !canRestoreCheckpoint()) return false;
    const { chat, index } = found;
    const message = $state.snapshot(found.message);

    const { undoCheckpointId } = await runRestore(chat, prepared, "Restore checkpoint");

    if (edit) {
        const removed = chat.messages.splice(index);
        chat.previousAttempts = [
            ...(chat.previousAttempts ?? []),
            { id: uuidv4(), atIndex: index, messages: $state.snapshot(removed), ts: Date.now() },
        ];
        chat.agentMessages = rewindAgentConversation($state.snapshot(chat.agentMessages), message.agentBefore);
        // Project and conversation are back at the same point: no note needed.
        delete chat.pendingRestoreNote;
        assistantState.composerDraft = message.content;
    } else {
        chat.pendingRestoreNote = RESTORE_NOTE;
    }

    chat.messages.push({
        id: uuidv4(),
        role: "event",
        type: "restore",
        prompt: titleFromPrompt(message.content, 60),
        skippedLockedGroups: prepared.skippedLockedGroups,
        undoCheckpointId,
        ts: Date.now(),
    });
    chat.updatedAt = Date.now();
    await saveChatQuietly(chat);
    return true;
}

/** Put back what a restore replaced (the checkpoint saved just before it). */
export async function completeRedoRestore(eventId, prepared) {
    const found = findActiveChatMessage(eventId);
    if (!found || !canRestoreCheckpoint()) return false;
    const { chat, message } = found;

    const { undoCheckpointId } = await runRestore(chat, prepared, "Redo checkpoint restore");

    message.redone = true;
    chat.pendingRestoreNote = RESTORE_NOTE;
    chat.messages.push({
        id: uuidv4(),
        role: "event",
        type: message.type === "restore" ? "redo_restore" : "restore",
        prompt: message.prompt,
        skippedLockedGroups: prepared.skippedLockedGroups,
        undoCheckpointId,
        ts: Date.now(),
    });
    chat.updatedAt = Date.now();
    await saveChatQuietly(chat);
    return true;
}

/** Save the checkpoint as a separate .ccprj file; the open project is untouched. */
export function downloadAssistantCheckpoint(checkpointId, suffix) {
    return downloadCheckpointCopy(checkpointId, { suffix });
}
